import {
  NetworkInformationAPI,
  NetworkMonitorConfig,
  NetworkQualityStatus,
} from "../components/network-status/networkStatusTypes";
import { DEFAULT_NETWORK_MONITOR_CONFIG } from "../components/network-status/networkStatusConstants";

/**
 * Reads browser NetworkInformation API values if supported.
 */
export const getBrowserConnectionInfo = (): {
  downlink?: number;
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  rtt?: number;
} | null => {
  if (typeof navigator === "undefined") return null;
  const navConn = (
    navigator as unknown as { connection?: NetworkInformationAPI }
  ).connection;
  if (!navConn) return null;

  return {
    downlink: navConn.downlink,
    effectiveType: navConn.effectiveType,
    rtt: navConn.rtt,
  };
};

/**
 * Performs an active HTTP probe to estimate real round-trip network latency.
 * Works seamlessly across Web, Electron, Wi-Fi, and LAN.
 */
export const measureProbeLatency = async (
  endpoint: string = DEFAULT_NETWORK_MONITOR_CONFIG.pingEndpoint,
): Promise<{ ok: boolean; latencyMs: number }> => {
  if (typeof window === "undefined") {
    return { ok: true, latencyMs: 50 };
  }

  if (!navigator.onLine) {
    return { ok: false, latencyMs: Infinity };
  }

  const startTime = performance.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout for responsive offline detection

  try {
    const cacheBuster = `_t=${Date.now()}&_r=${Math.random().toString(36).substring(7)}`;
    const separator = endpoint.includes("?") ? "&" : "?";
    const url = `${endpoint}${separator}${cacheBuster}`;

    // mode: 'no-cors' allows probing any endpoint (including Google 204 or local server) without CORS errors
    await fetch(url, {
      method: "GET",
      cache: "no-store",
      mode: "no-cors",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const latencyMs = Math.round(performance.now() - startTime);
    return { ok: true, latencyMs };
  } catch {
    clearTimeout(timeoutId);
    return { ok: false, latencyMs: Infinity };
  }
};

/**
 * Evaluates current network metrics against configured thresholds.
 */
export const evaluateNetworkQuality = (
  isOnline: boolean,
  connectionInfo: {
    downlink?: number;
    effectiveType?: string;
    rtt?: number;
  } | null,
  probeLatencyMs: number | null,
  config: NetworkMonitorConfig = DEFAULT_NETWORK_MONITOR_CONFIG,
): {
  status: NetworkQualityStatus;
  rttMs?: number;
  downlinkSpeedMbps?: number;
  speedReductionPercent?: number;
  expectedSpeedMbps?: number;
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
} => {
  if (!isOnline) {
    return {
      status: "OFFLINE",
      speedReductionPercent: 100,
      expectedSpeedMbps: config.expectedSpeedMbps,
    };
  }

  const downlink = connectionInfo?.downlink;
  const effectiveType = connectionInfo?.effectiveType as
    | "slow-2g"
    | "2g"
    | "3g"
    | "4g"
    | undefined;
  const apiRtt = connectionInfo?.rtt;
  const measuredRtt =
    probeLatencyMs !== null && Number.isFinite(probeLatencyMs)
      ? probeLatencyMs
      : apiRtt;

  // Calculate speed reduction percentage against expected baseline speed (e.g. config.expectedSpeedMbps)
  let speedReductionPercent: number | undefined;
  if (downlink !== undefined && Number.isFinite(downlink)) {
    const rawReduction =
      ((config.expectedSpeedMbps - downlink) / config.expectedSpeedMbps) * 100;
    speedReductionPercent = Math.max(
      0,
      Math.min(100, Math.round(rawReduction)),
    );
  } else if (measuredRtt !== undefined && Number.isFinite(measuredRtt)) {
    // Latency-based reduction estimation fallback
    if (
      measuredRtt > config.poorLatencyThresholdMs ||
      effectiveType === "slow-2g"
    ) {
      speedReductionPercent = 65; // Estimated > 50% reduction
    } else if (
      measuredRtt > config.slowLatencyThresholdMs ||
      effectiveType === "2g" ||
      effectiveType === "3g"
    ) {
      speedReductionPercent = 40; // Estimated 30-50% reduction
    } else {
      speedReductionPercent = 0;
    }
  }

  // Determine quality tier based on percentage reduction and latency thresholds
  const isPoor =
    (speedReductionPercent !== undefined &&
      speedReductionPercent > config.poorReductionThresholdPercent) ||
    (measuredRtt !== undefined &&
      measuredRtt > config.poorLatencyThresholdMs) ||
    effectiveType === "slow-2g";

  const isSlow =
    !isPoor &&
    ((speedReductionPercent !== undefined &&
      speedReductionPercent > config.slowReductionThresholdPercent) ||
      (measuredRtt !== undefined &&
        measuredRtt > config.slowLatencyThresholdMs) ||
      (downlink !== undefined && downlink < config.slowDownlinkThresholdMbps) ||
      effectiveType === "2g" ||
      effectiveType === "3g");

  let status: NetworkQualityStatus = "GOOD";
  if (isPoor) {
    status = "POOR";
  } else if (isSlow) {
    status = "SLOW";
  }

  return {
    status,
    downlinkSpeedMbps: downlink,
    speedReductionPercent,
    expectedSpeedMbps: config.expectedSpeedMbps,
    effectiveType,
    rttMs:
      measuredRtt !== undefined && Number.isFinite(measuredRtt)
        ? Math.round(measuredRtt)
        : undefined,
  };
};

/**
 * Checks whether enough time has passed to permit firing a throttled notification.
 */
export const shouldTriggerNotification = (
  lastNotificationTime: number,
  cooldownMs: number = DEFAULT_NETWORK_MONITOR_CONFIG.notificationCooldownMs,
): boolean => {
  return Date.now() - lastNotificationTime >= cooldownMs;
};
